/* eslint-disable react/prop-types */
import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";

// * Component imports
import FixedBanner from "../../components/FixedBanner/FixedBanner";
import WarningIcon from "@mui/icons-material/Warning";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import PriorityHighIcon from "@mui/icons-material/PriorityHigh";
import BoltIcon from "@mui/icons-material/Bolt";

// * Environment Imports
import { environment } from "../../environments/environment";

// * Axios Imports
import axios from "axios";

// * Import Context
import { useAuthContext } from "../../hooks/useAuthContext";

function FeedbackPage() {
  document.title = `Feedback | LeadZLab`;

  // * Plan details
  const [plan, setPlan] = useState("");
  const [trialValidUntil, setTrialValidUntil] = useState(false);

  // * Nudges
  const [quota, setQuota] = useState({});

  // * Auth context
  const { user } = useAuthContext();

  function calculateDateDifference(timestamp) {
    const now = new Date();
    const targetDate = new Date(timestamp);

    // Check if the target date is in the past
    if (targetDate < now) {
      return -1;
    }

    const timeDifference = targetDate - now;
    const daysDifference = Math.floor(timeDifference / (1000 * 60 * 60 * 24));

    return daysDifference;
  }

  // * Fetch plan
  const getPlan = async () => {
    try {
      const { data } = await axios.get(`${environment.BACKEND}/user`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${user?.accessToken}`,
        },
      });
      const userData = data.data;
      setPlan(userData.plan);

      setTrialValidUntil(
        calculateDateDifference(userData.subscriptionActiveTill)
      );
    } catch (err) {
      console.log(err.response.data);
    }
  };

  // * Check Quota
  const checkQuota = async () => {
    try {
      const { data } = await axios.get(`${environment.BACKEND}/user/quota`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${user?.accessToken}`,
        },
      });
      setQuota(data.data);
    } catch (err) {
      console.log(err.response.data);
    }
  };

  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");

  // * Submit feedback form
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      let { data } = await axios.post(
        `${environment.BACKEND}/user/submit-feedback`,
        {
          subject: subject,
          body: body,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${user?.accessToken}`,
          },
        }
      );
      toast.success(data.message, {
        position: "top-right",
        autoClose: true,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: 0,
      });
    } catch (err) {
      toast.error(`${err?.response?.data?.message ?? err.message}`, {
        position: "top-right",
        autoClose: true,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: 0,
      });
    }
  };

  useEffect(() => {
    getPlan();
    checkQuota();
  }, []);

  return (
    <div className="space-y-8">
      {plan === "free" ? (
        trialValidUntil < 0 ? (
          <FixedBanner
            icon={<WarningIcon />}
            message={`Your free trial has expired. Please upgrade now to continue generating leads`}
            actionText="Upgrade"
            actionLink="/pricing"
            color="red"
          />
        ) : (
          <FixedBanner
            icon={<BoltIcon />}
            message={`Your free trial expires in ${trialValidUntil} days`}
            actionText="Upgrade"
            actionLink="/pricing"
            color="emerald"
          />
        )
      ) : plan === "" ? null : (
        <FixedBanner
          icon={<VerifiedUserIcon />}
          message={`You are currently on the ${plan?.toUpperCase()} plan`}
          // actionText="Upgrade"
          // actionLink="/pricing"
          color="yellow"
        />
      )}
      {/* Job limit nudge */}
      {quota.jobsCreated >= quota.jobsAllowed ? (
        <FixedBanner
          icon={<PriorityHighIcon />}
          message={`You’ve reached your scout limit. Upgrade to create more. (${quota.jobsCreated}/${quota.jobsAllowed})`}
          actionText="Upgrade"
          actionLink="/pricing"
          color="gray"
        />
      ) : null}
      <div>
        <h1 className="text-xl font-bold text-slate-900">Feedback</h1>
        <p className="mt-1 text-slate-600">
          Share your thoughts, feature requests, or suggestions. We read everything.
        </p>
        <p className="mt-2 text-sm text-slate-600">Contact: support@leadzlab.co</p>
      </div>
      <form onSubmit={handleSubmit} className="w-full rounded-xl border border-slate-200 bg-white p-6 space-y-4">
        <div className="w-full">
          <label htmlFor="subject" className="block text-sm font-medium text-slate-700 mb-1">Subject</label>
          <input
            type="text"
            id="subject"
            className="input-base w-full"
            placeholder="e.g. Feature request: …"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            required
          />
        </div>
        <div className="w-full">
          <label htmlFor="body" className="block text-sm font-medium text-slate-700 mb-1">Message</label>
          <textarea
            id="body"
            placeholder="Tell us what’s on your mind…"
            className="input-base min-h-[120px] w-full resize-y"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={5}
            required
          />
        </div>
        <button type="submit" className="w-full rounded-xl bg-emerald-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-emerald-700">
          Send feedback
        </button>
      </form>
    </div>
  );
}

export default FeedbackPage;
